import { useEffect, useRef, useState } from 'react';
import { typeAheadCall } from '../../../../../Redux/Reducers/SearchSlice';
import { useAppDispatch } from '../../../../../Hooks/Hooks';
import TrimbleMaps, { LngLatBoundsLike } from '@trimblemaps/trimblemaps-js';
import { removeInnerElements } from '../../../../../Utils/Basic';
import { KEYS } from '../../../../../Constants/Constants';
import { TLoadMapContainerProps } from './LoadMapContainer.type';
import CustomModal from '../../../CustomModal/CustomModal';
import './LoadMapContainer.scss';

const getCenterPoint = (stops: any[]) => ({
  lat: (stops[0]?.lat + stops[1]?.lat) / 2,
  lng: (stops[0]?.lng + stops[1]?.lng + (stops[1]?.lng - stops[0]?.lng > 180 ? -360 : 0)) / 2,
});
const LoadMapContainer = (props: TLoadMapContainerProps) => {
  const dispatch = useAppDispatch();
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const [stopsCoords, setStopsCoords] = useState<any[]>([]);
  const [expandedModalProps, setExpandedModalProps] = useState<any>({
    isModalOpen: false,
  });
  const { data, showZoomControls = true, showExpandedView = false } = props;
  const MAP_CONTAINER_DOM = useRef<HTMLDivElement>(null);
  const EXPANDED_MAP_CONTAINER_DOM = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (MAP_CONTAINER_DOM.current) {
        MAP_CONTAINER_DOM.current.innerHTML = '';
      }
      if (EXPANDED_MAP_CONTAINER_DOM.current) {
        EXPANDED_MAP_CONTAINER_DOM.current.innerHTML = '';
      }
    };
  }, []);
  // if (!KEYS.PCMILER_API_KEY) {
  //   console.error("API Key error");
  //   return null;
  // }
  // if (!data?.loadStop?.length) {
  //   console.error("Load stop data error");
  //   return null;
  // }

  useEffect(() => {
    const fetchLatLong = async (locationData: string) => {
      let coords;
      const LocationCallback: any = async (res: any) => {
        if (res?.Locations[0]?.Coords) {
          coords = new TrimbleMaps.LngLat(
            res?.Locations[0]?.Coords?.Lon,
            res?.Locations[0]?.Coords?.Lat,
          );
        } else {
          coords = new TrimbleMaps.LngLat(0, 0);
        }
      };
      await dispatch(
        typeAheadCall({
          queryPayloads: { query: locationData },
          callback: LocationCallback,
        }),
      );
      return coords;
    };
    const fetchStopsCoords = async () => {
      const loadStop = data?.loadStop || data?.shipmentStops;
      const coords = await Promise.all(
        loadStop?.map(async (stop: any) => {
          if (stop?.latLang) {
            return stop.latLang;
          } else if (stop?.longitude && stop?.latitude) {
            const latLang = { lng: stop?.longitude, lat: stop?.latitude };
            return latLang;
          } else {
            const address = [
              stop?.addressLine1 ?? ' ',
              stop?.addressLine2 ?? ' ',
              stop?.city,
              stop?.state,
              stop?.postalCode,
            ]
              ?.join(',')
              ?.replaceAll(' ,', '');
            const response = await fetchLatLong(address);
            return response;
          }
        }),
      );
      setStopsCoords(coords);
    };
    fetchStopsCoords();
  }, [data?.loadStop, data?.shipmentStops, dispatch]);

  if (MAP_CONTAINER_DOM.current) TrimbleMaps.APIKey = KEYS.PCMILER_API_KEY;
  if (EXPANDED_MAP_CONTAINER_DOM.current) TrimbleMaps.APIKey = KEYS.PCMILER_API_KEY;

  useEffect(() => {
    if (stopsCoords?.length) {
      const getIconSrc = (stopSequence: number) => {
        if (stopSequence === 1) {
          return '/oo/assets/icon/origin_icon.svg';
        } else if (
          stopSequence === stopsCoords.length ||
          (stopsCoords.length === 2 && stopSequence === 2) ||
          stopsCoords.length === 1
        ) {
          return '/oo/assets/icon/destination_icon.svg';
        } else {
          return '/oo/assets/icon/stop-pointer.svg';
        }
      };

      const renderMarker = (stopSequence: number): HTMLElement => {
        const icon = document.createElement('div');
        let img = document.createElement('img');
        img.setAttribute('src', getIconSrc(stopSequence));
        img.setAttribute(
          'height',
          getIconSrc(stopSequence) === '/oo/assets/icon/origin_icon.svg' ||
            getIconSrc(stopSequence) === '/oo/assets/icon/destination_icon.svg'
            ? '24'
            : '12',
        );
        img.setAttribute(
          'width',
          getIconSrc(stopSequence) === '/oo/assets/icon/origin_icon.svg' ||
            getIconSrc(stopSequence) === '/oo/assets/icon/destination_icon.svg'
            ? '24'
            : '12',
        );
        img.setAttribute('alt', 'Stop_indicator_icon');
        icon.appendChild(img);
        return icon;
      };

      const loadMap = new TrimbleMaps.Map({
        container: expandedModalProps?.isModalOpen
          ? EXPANDED_MAP_CONTAINER_DOM.current!
          : MAP_CONTAINER_DOM.current!,
        style:
          stopsCoords?.length === 1
            ? TrimbleMaps.Common.Style.SATELLITE
            : TrimbleMaps.Common.Style.TRANSPORTATION,
        center: getCenterPoint(removeInnerElements(stopsCoords)),
        bounds: removeInnerElements(stopsCoords) as LngLatBoundsLike,
        zoom: 2,
        hash: true,
      });

      loadMap.on('load', function () {
        const mapMarker = (stop: number) =>
          new TrimbleMaps.Marker({
            element: renderMarker(stop),
          });

        !expandedModalProps?.isModalOpen &&
          showZoomControls &&
          loadMap.addControl(
            new TrimbleMaps.NavigationControl({
              showCompass: false,
            }),
          );

        const loadStop = data?.loadStop || data?.shipmentStops;
        const isLimitedZoom =
          loadStop?.map((stop: any) => !stop?.addressLine1 && !stop?.addressLine2) || [];
        if (isLimitedZoom.includes(true)) {
          loadMap.setMaxZoom(10);
        } else loadMap.setMaxZoom(stopsCoords.length === 1 ? 17 : 15);
        const stopSequence = loadStop?.map(
          (stop: any, _idx: any) => stop.stopSequence || parseInt(stop.sequenceNo),
        );

        const stopSequenceMap = stopSequence.reduce((acc: any, seq: number, index: number) => {
          acc[seq] = stopsCoords[index];
          return acc;
        }, {});
        const sortedCoords = Object.keys(stopSequenceMap)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map((seq) => stopSequenceMap[seq]);

        sortedCoords.forEach((stop: any, index: any) => {
          mapMarker(index + 1)
            .setLngLat(stop)
            .addTo(loadMap);
        });
        const mapRoute = new TrimbleMaps.Route({
          routeId: 'mapRoute',
          stops: sortedCoords,
          showStops: false,
          routeColor: '#80AEE6',
        });
        mapRoute.addTo(loadMap);
        setIsMapLoaded(true);
      });
    }
  }, [stopsCoords, expandedModalProps?.isModalOpen, showZoomControls]);

  const handleExpandMapClick = () => {
    setExpandedModalProps({
      isModalOpen: true,
      closeModalAction: () => setExpandedModalProps({ isModalOpen: false }),
      contentAlignment: 'left',
      additionalCssClass: 'Expanded_Map_Modal',
      size: 'large',
      closeBtn: true,
      children: (
        <div
          datatest-id="Expanded_Map_Modal"
          className="LoadMapContainer"
          ref={EXPANDED_MAP_CONTAINER_DOM}
        />
      ),
    });
  };

  return (
    <>
      <div className="LoadMapContainer" data-testid="LoadMapContainer" ref={MAP_CONTAINER_DOM}>
        {showExpandedView && isMapLoaded && (
          <div
            data-testid="Expanded_Button"
            className="Expanded_View_Btn"
            onClick={handleExpandMapClick}
          >
            <img className="Expand_Map_Logo" src="/oo/assets/icon/expand.svg" alt="Expand_Map" />
            <span>Expand map</span>
          </div>
        )}
      </div>
      <CustomModal {...expandedModalProps} />
    </>
  );
};

export default LoadMapContainer;
